import React from 'react';
import { Link } from 'react-router-dom';
import { trackAppStoreClick } from '../utils/analytics';

const Home: React.FC = () => {
  return (
    <div className='min-h-screen bg-gradient-to-b from-blue-50 to-white'>
      {/* Hero Section */}
      <header className='container mx-auto px-4 py-16 text-center'>
        <div className='mb-8'>
          <img
            src='/starjar-icon.png'
            alt='StarJar Logo'
            className='w-32 h-32 mx-auto mb-6 rounded-3xl shadow-lg'
          />
          <h1 className='text-6xl font-bold text-gray-900 mb-4'>
            Star<span className='text-primary'>Jar</span>
          </h1>
          <p className='text-2xl text-gray-600 mb-8'>
            Reward good behavior, track progress, celebrate growth
          </p>
          <p className='text-lg text-gray-500 max-w-2xl mx-auto'>
            A family-friendly app to help parents motivate children with
            positive reinforcement. Set goals, earn stars, and unlock rewards!
          </p>
        </div>

        <div className='mt-12'>
          <a
            href={process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/app/starjar/id6754808380'}
            className='inline-block bg-primary hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-colors shadow-lg'
            onClick={() => trackAppStoreClick('ios')}
          >
            Download on the App Store
          </a>
        </div>
      </header>

      {/* Features Section */}
      <section className='container mx-auto px-4 py-16'>
        <h2 className='text-4xl font-bold text-center mb-12 text-gray-900'>
          Features
        </h2>

        <div className='grid md:grid-cols-3 gap-8'>
          <FeatureCard
            icon='⭐'
            title='Star Tracking'
            description='Earn stars for positive behaviors and track progress towards goals'
          />
          <FeatureCard
            icon='🎁'
            title='Custom Rewards'
            description='Create personalized rewards that motivate your children'
          />
          <FeatureCard
            icon='👨‍👩‍👧‍👦'
            title='Multiple Profiles'
            description='Manage jars for multiple children with unlimited profiles (Premium)'
          />
          <FeatureCard
            icon='📊'
            title='Progress Insights'
            description='View detailed charts and track behavior patterns over time'
          />
          <FeatureCard
            icon='☁️'
            title='iCloud Sync'
            description='Keep data synchronized across all your devices'
          />
          <FeatureCard
            icon='🔒'
            title='PIN Protection'
            description='Secure parental controls with biometric authentication'
          />
        </div>
      </section>

      {/* Pricing Section */}
      <section className='bg-gray-50 py-16'>
        <div className='container mx-auto px-4'>
          <h2 className='text-4xl font-bold text-center mb-12 text-gray-900'>
            Simple Pricing
          </h2>

          <div className='max-w-4xl mx-auto grid md:grid-cols-2 gap-8'>
            <PricingCard
              title='Free'
              price='$0'
              features={[
                '1 Jar (Profile)',
                'Up to 10 Behaviors',
                'Up to 5 Rewards',
                'iCloud Sync',
                'Progress Insights',
                'PIN Protection',
              ]}
            />
            <PricingCard
              title='Premium'
              price='£1.99/year'
              featured
              features={[
                'Unlimited Jars',
                'Unlimited Behaviors',
                'Unlimited Rewards',
                'Data Export',
                'Priority Support',
                'All Free Features',
              ]}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='bg-gray-900 text-white py-12'>
        <div className='container mx-auto px-4 text-center'>
          <div className='mb-6'>
            <h3 className='text-2xl font-bold mb-4'>StarJar</h3>
            <p className='text-gray-400'>
              Making parenting easier, one star at a time
            </p>
          </div>

          <div className='flex justify-center gap-8 mb-6'>
            <Link
              to='/privacy'
              className='text-gray-400 hover:text-white transition-colors'
            >
              Privacy Policy
            </Link>
            <Link
              to='/terms'
              className='text-gray-400 hover:text-white transition-colors'
            >
              Terms of Service
            </Link>
            <a
              href='mailto:starjar@twinkle.hk'
              className='text-gray-400 hover:text-white transition-colors'
            >
              Contact Support
            </a>
          </div>

          <p className='text-gray-500 text-sm'>
            © 2025 StarJar. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
}) => {
  return (
    <div className='bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow'>
      <div className='text-5xl mb-4'>{icon}</div>
      <h3 className='text-xl font-bold mb-2 text-gray-900'>{title}</h3>
      <p className='text-gray-600'>{description}</p>
    </div>
  );
};

interface PricingCardProps {
  title: string;
  price: string;
  features: string[];
  featured?: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  price,
  features,
  featured,
}) => {
  return (
    <div
      className={`bg-white p-8 rounded-lg shadow-lg ${
        featured ? 'ring-4 ring-primary' : ''
      }`}
    >
      {featured && (
        <div className='bg-primary text-white text-sm font-bold py-1 px-4 rounded-full inline-block mb-4'>
          RECOMMENDED
        </div>
      )}
      <h3 className='text-2xl font-bold mb-2 text-gray-900'>{title}</h3>
      <p className='text-4xl font-bold mb-6 text-primary'>{price}</p>
      <ul className='space-y-3 mb-8'>
        {features.map((feature, index) => (
          <li key={index} className='flex items-center text-gray-700'>
            <span className='text-green-500 mr-2'>✓</span>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Home;
